import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import "./adminTable.css";

const SECTION_CONFIG = [
  {
    applicationCode: "DB_EDITOR",
    title: "DB Editor",
    listEndpoint: "/admin/getAllUsers/DB_EDITOR",
    roleOptions: ["Read Only", "Write User"],
    isAdminSection: false,
  },
  {
    applicationCode: "PARTS_CATALOG",
    title: "Parts Catalog Editor",
    listEndpoint: "/admin/getAllUsers/PARTS_CATALOG",
    roleOptions: ["Read Only", "Write User"],
    isAdminSection: false,
  },
  {
    applicationCode: "ADMIN",
    title: "Admin",
    listEndpoint: "/admin/getAllAdmins",
    roleOptions: ["Admin"],
    isAdminSection: true,
  },
];

function toRolePayload(sectionConfig, roleLabel) {
  if (sectionConfig.isAdminSection) {
    return { readRole: "Y", writeRole: "Y", adminRole: "Y" };
  }

  return roleLabel === "Write User"
    ? { readRole: "Y", writeRole: "Y", adminRole: "N" }
    : { readRole: "Y", writeRole: "N", adminRole: "N" };
}

function toRoleLabel(sectionConfig, user) {
  if (sectionConfig.isAdminSection) {
    return "Admin";
  }

  const parsedRoleId = Number(user?.roleId);
  if (Number.isFinite(parsedRoleId)) {
    return parsedRoleId === 2 ? "Write User" : "Read Only";
  }
  return user.writeUser ? "Write User" : "Read Only";
}

function AdminSection({ sectionConfig, loggedInUsername, setGlobalPopup }) {
  const linkedApplicationCodesForAdmin = ["DB_EDITOR", "PARTS_CATALOG"];
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsernames, setSelectedUsernames] = useState([]);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [editUsername, setEditUsername] = useState("");
  const [editRole, setEditRole] = useState(sectionConfig.roleOptions[0]);
  const [editUsernameError, setEditUsernameError] = useState("");

  const [newUsername, setNewUsername] = useState("");
  const [newRole, setNewRole] = useState(sectionConfig.roleOptions[0]);
  const [addUsernameError, setAddUsernameError] = useState("");

  const normalizeListRow = (user) => {
    if (typeof user === "string") {
      return {
        username: user,
        readRole: sectionConfig.isAdminSection ? "Y" : "Y",
        writeRole: sectionConfig.isAdminSection ? "Y" : "N",
        roleLabel: sectionConfig.isAdminSection ? "Admin" : "Read Only",
        createdBy: "",
        updatedBy: "",
      };
    }

    const parsedRoleId = Number(user?.roleId);
    const roleId = Number.isFinite(parsedRoleId) ? parsedRoleId : null;
    const readFromRoleId = roleId === 1;
    const writeFromRoleId = roleId === 2;

    return {
      username: user.username,
      readRole:
        roleId !== null
          ? (readFromRoleId ? "Y" : "N")
          : (user.readUser ? "Y" : "N"),
      writeRole:
        roleId !== null
          ? (writeFromRoleId ? "Y" : "N")
          : (user.writeUser ? "Y" : "N"),
      roleLabel: toRoleLabel(sectionConfig, user),
      createdBy: user.createdBy ?? "",
      updatedBy: user.updatedBy ?? "",
    };
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth({
        method: "GET",
        endpoint: sectionConfig.listEndpoint,
      });

      const rows = Array.isArray(data) ? data : data?.users ?? data?.content ?? [];
      const formatted = rows.map((user) => normalizeListRow(user));

      setUserData(formatted);
    } catch (error) {
      console.error(`Failed to fetch users for ${sectionConfig.applicationCode}:`, error);
    } finally {
      setLoading(false);
    }
  }, [sectionConfig]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCheckboxChange = (username) => {
    setSelectedUsernames((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  const handleAdd = () => {
    setNewUsername("");
    setNewRole(sectionConfig.roleOptions[0]);
    setAddUsernameError("");
    setIsAddModalOpen(true);
  };

  const handleEdit = () => {
    if (selectedUsernames.length !== 1) {
      setGlobalPopup({
        message: `Please select exactly one user to edit in ${sectionConfig.title}.`,
        show: true,
      });
      return;
    }

    const selectedUser = userData.find(
      (user) => user.username === selectedUsernames[0]
    );
    if (!selectedUser) {
      return;
    }

    setEditUsername(selectedUser.username);
    setEditRole(selectedUser.roleLabel);
    setEditUsernameError("");
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUsernames.length) return;
    setLoading(true);
    try {
      await fetchWithAuth({
        method: "DELETE",
        endpoint: `/admin/deleteUser/${sectionConfig.applicationCode}`,
        headers: { "Content-Type": "application/json" },
        data: selectedUsernames,
      });

      if (sectionConfig.isAdminSection) {
        for (const appCode of linkedApplicationCodesForAdmin) {
          await fetchWithAuth({
            method: "DELETE",
            endpoint: `/admin/deleteUser/${appCode}`,
            headers: { "Content-Type": "application/json" },
            data: selectedUsernames,
          });
        }
      }

      setGlobalPopup({
        message: `${sectionConfig.title}: user(s) deleted successfully!`,
        show: true,
        onClose: () => {
          setSelectedUsernames([]);
          fetchUsers();
        },
      });
    } catch (error) {
      console.error(`Error deleting users for ${sectionConfig.applicationCode}:`, error);
      setGlobalPopup({
        message: `${sectionConfig.title}: error deleting users.`,
        show: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRole = async () => {
    if (!editUsername.trim()) {
      setEditUsernameError("Username is required.");
      return;
    }

    setEditUsernameError("");
    const originalUsername = selectedUsernames[0];
    const rolePayload = toRolePayload(sectionConfig, editRole);

    setModalLoading(true);
    try {
      await fetchWithAuth({
        method: "PUT",
        endpoint: `/admin/updateUser/${sectionConfig.applicationCode}/${encodeURIComponent(originalUsername)}`,
        headers: { "Content-Type": "application/json" },
        data: {
          username: editUsername,
          ...rolePayload,
          updatedBy: loggedInUsername,
          updatedTs: new Date().toISOString(),
        },
      });

      if (sectionConfig.isAdminSection) {
        for (const appCode of linkedApplicationCodesForAdmin) {
          await fetchWithAuth({
            method: "PUT",
            endpoint: `/admin/updateUser/${appCode}/${encodeURIComponent(originalUsername)}`,
            headers: { "Content-Type": "application/json" },
            data: {
              username: editUsername,
              readRole: "Y",
              writeRole: "Y",
              adminRole: "Y",
              updatedBy: loggedInUsername,
              updatedTs: new Date().toISOString(),
            },
          });
        }
      }

      setIsEditModalOpen(false);
      setGlobalPopup({
        message: `${sectionConfig.title}: user updated successfully!`,
        show: true,
        onClose: () => {
          setSelectedUsernames([]);
          fetchUsers();
        },
      });
    } catch (error) {
      console.error(`Error updating user for ${sectionConfig.applicationCode}:`, error);
      setGlobalPopup({
        message: `${sectionConfig.title}: error updating user.`,
        show: true,
      });
    } finally {
      setModalLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUsername.trim()) {
      setAddUsernameError("Username is required.");
      return;
    }

    setAddUsernameError("");
    const rolePayload = toRolePayload(sectionConfig, newRole);

    setModalLoading(true);
    try {
      const data = await fetchWithAuth({
        method: "POST",
        endpoint: `/admin/addUser/${sectionConfig.applicationCode}`,
        headers: { "Content-Type": "application/json" },
        data: {
          username: newUsername,
          ...rolePayload,
          createdBy: loggedInUsername,
          updatedBy: loggedInUsername,
        },
      });

      const message =
        typeof data === "string"
          ? data
          : data.message || "User added successfully!";

      setIsAddModalOpen(false);
      setGlobalPopup({
        message,
        show: true,
        onClose: () => fetchUsers(),
      });
    } catch (error) {
      console.error(`Error adding user for ${sectionConfig.applicationCode}:`, error);
      const resp = error?.response?.data;
      const serverMsg =
        typeof resp === "string"
          ? resp
          : resp?.message ?? null;
      setGlobalPopup({
        message:
          serverMsg ??
          `${sectionConfig.title}: an unexpected error occurred while adding the user.`,
        show: true,
      });
    } finally {
      setModalLoading(false);
    }
  };

  const filteredData = userData.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <section className="adminSectionCard">
        {loading ? (
          <>
            <div className="blur-background"></div>
            <div className="loading"></div>
          </>
        ) : (
          <>
            <div className="adminTableWrapper">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th></th>
                    <th>Username</th>
                    {!sectionConfig.isAdminSection && (
                      <>
                        <th>Read Role</th>
                        <th>Write User</th>
                      </>
                    )}
                    <th>Created By</th>
                    {!sectionConfig.isAdminSection && <th>Updated By</th>}
                  </tr>
                  <tr>
                    <td></td>
                    <td>
                      <input
                        type="text"
                        placeholder="Search Username"
                        className="adminSearchInput"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </td>
                    {!sectionConfig.isAdminSection && (
                      <>
                        <td></td>
                        <td></td>
                      </>
                    )}
                    <td></td>
                    {!sectionConfig.isAdminSection && <td></td>}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsernames.includes(row.username)}
                          onChange={() => handleCheckboxChange(row.username)}
                        />
                      </td>
                      <td>{row.username}</td>
                      {!sectionConfig.isAdminSection && (
                        <>
                          <td>{row.readRole}</td>
                          <td>{row.writeRole}</td>
                        </>
                      )}
                      <td>{row.createdBy}</td>
                      {!sectionConfig.isAdminSection && <td>{row.updatedBy}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="adminTableActions">
              <button onClick={handleAdd}>Add User</button>
              {!sectionConfig.isAdminSection && (
                <button
                  onClick={handleEdit}
                  disabled={selectedUsernames.length !== 1}
                >
                  Update User
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={selectedUsernames.length === 0}
              >
                Delete User
              </button>
            </div>
          </>
        )}
      </section>

      {isEditModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Update User - {sectionConfig.title}</h3>
            <input
              type="text"
              placeholder="Enter username"
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
              className={editUsernameError ? "inputError" : ""}
              disabled={modalLoading}
            />
            {editUsernameError && (
              <p className="errorText">{editUsernameError}</p>
            )}
            <div className="roleSelection">
              {sectionConfig.roleOptions.map((roleOption) => (
                <label key={`edit-${sectionConfig.applicationCode}-${roleOption}`}>
                  <input
                    type="radio"
                    name={`editRole-${sectionConfig.applicationCode}`}
                    value={roleOption}
                    checked={editRole === roleOption}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={modalLoading || sectionConfig.isAdminSection}
                  />
                  {roleOption}
                </label>
              ))}
            </div>
            <div className="modalActions">
              <button onClick={handleSaveRole} disabled={modalLoading}>
                {modalLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
              <button onClick={() => setIsEditModalOpen(false)} disabled={modalLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Add User - {sectionConfig.title}</h3>
            <input
              type="text"
              placeholder="Enter username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className={addUsernameError ? "inputError" : ""}
              disabled={modalLoading}
            />
            {addUsernameError && (
              <p className="errorText">{addUsernameError}</p>
            )}
            <div className="roleSelection">
              {sectionConfig.roleOptions.map((roleOption) => (
                <label key={`new-${sectionConfig.applicationCode}-${roleOption}`}>
                  <input
                    type="radio"
                    name={`newRole-${sectionConfig.applicationCode}`}
                    value={roleOption}
                    checked={newRole === roleOption}
                    onChange={(e) => setNewRole(e.target.value)}
                    disabled={modalLoading || sectionConfig.isAdminSection}
                  />
                  {roleOption}
                </label>
              ))}
            </div>
            <div className="modalActions">
              <button onClick={handleCreateUser} disabled={modalLoading}>
                {modalLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </button>
              <button onClick={() => setIsAddModalOpen(false)} disabled={modalLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminTable() {
  const loggedInUsername = localStorage.getItem("username") || "unknown";
  const [activeApplicationCode, setActiveApplicationCode] = useState(
    SECTION_CONFIG[0].applicationCode
  );
  const [popup, setPopup] = useState({
    message: "",
    show: false,
    onClose: null,
  });

  const activeSectionConfig =
    SECTION_CONFIG.find(
      (section) => section.applicationCode === activeApplicationCode
    ) || SECTION_CONFIG[0];

  return (
    <>
      <div className="adminSectionSwitcher">
        {SECTION_CONFIG.map((sectionConfig) => (
          <label key={sectionConfig.applicationCode} className="adminSwitchOption">
            <input
              type="radio"
              name="adminSection"
              value={sectionConfig.applicationCode}
              checked={activeApplicationCode === sectionConfig.applicationCode}
              onChange={() => setActiveApplicationCode(sectionConfig.applicationCode)}
            />
            {sectionConfig.title}
          </label>
        ))}
      </div>
      <AdminSection
        key={activeSectionConfig.applicationCode}
        sectionConfig={activeSectionConfig}
        loggedInUsername={loggedInUsername}
        setGlobalPopup={setPopup}
      />

      {popup.show && (
        <div className="modalOverlay">
          <div className="modalContent">
            <p>{popup.message}</p>
            <button
              onClick={() => {
                const onClose = popup.onClose;
                setPopup({ message: "", show: false, onClose: null });
                if (onClose) {
                  onClose();
                }
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
